// Mock data for Procurement Demo Module
// This data supports the demo story of a microprocessor chip manufacturer

// ============ DATE HELPERS ============
// All dates are calculated relative to "today" so the demo stays current

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatDate(date);
}

function daysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatDate(date);
}

function weeksAgo(weeks: number): string {
  return daysAgo(weeks * 7);
}

function weeksFromNow(weeks: number): string {
  return daysFromNow(weeks * 7);
}

function monthsAgo(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return formatDate(date);
}

function monthsFromNow(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return formatDate(date);
}

export interface Supplier {
  id: string;
  name: string;
  contactEmail: string;
  phone: string;
  location: string;
  leadTimeWeeks: number;
  qualityScore: number; // 1-100
  reliabilityScore: number; // 1-100 (on-time delivery rate)
  status: 'active' | 'inactive' | 'pending';
  itemsSupplied: string[]; // item IDs
  priceCompetitiveness: 'low' | 'medium' | 'high';
  lastOrderDate: string;
  totalOrders: number;
}

export interface Item {
  id: string;
  name: string;
  sku: string;
  category: 'raw-material' | 'component' | 'packaging';
  description: string;
  unitOfMeasure: string;
  unitCost: number;
  leadTimeWeeks: number;
  minimumOrderQuantity: number;
  suppliers: string[]; // supplier IDs
  requiredPerUnit: number; // How many needed per finished microprocessor
}

export interface InventoryItem {
  id: string;
  itemId: string;
  currentStock: number;
  reservedStock: number;
  availableStock: number;
  reorderPoint: number;
  safetyStock: number;
  lastRestockDate: string;
  location: string;
}

export interface PurchaseOrderAttachment {
  name: string;
  url: string;
  type: 'pdf' | 'image' | 'document';
}

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  status:
    | 'draft'
    | 'pending'
    | 'approved'
    | 'sent'
    | 'confirmed'
    | 'shipped'
    | 'received'
    | 'cancelled';
  items: PurchaseOrderItem[];
  totalAmount: number;
  createdAt: string;
  expectedDelivery: string;
  deliveredOn?: string; // For received orders
  notes?: string;
  aiGenerated: boolean;
  attachments?: PurchaseOrderAttachment[];
}

export interface PurchaseOrderItem {
  itemId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface SalesForecast {
  period: string;
  forecastedUnits: number;
  confidence: number; // percentage
}

export interface SupplierQuote {
  supplierId: string;
  itemId: string;
  pricePerUnit: number;
  minimumQuantity: number;
  validUntil: string;
  leadTimeWeeks: number;
}

// ============ SUPPLIERS ============
export const mockSuppliers: Supplier[] = [
  {
    id: 'sup-001',
    name: 'Taiwan Semiconductor Materials Co.',
    contactEmail: 'sales@tsmc-materials.com',
    phone: '+886-2-2555-1234',
    location: 'Hsinchu, Taiwan',
    leadTimeWeeks: 6,
    qualityScore: 95,
    reliabilityScore: 92,
    status: 'active',
    itemsSupplied: ['item-001', 'item-002'],
    priceCompetitiveness: 'medium',
    lastOrderDate: weeksAgo(5), // ~5 weeks ago
    totalOrders: 45,
  },
  {
    id: 'sup-002',
    name: 'Silicon Valley Wafers Inc.',
    contactEmail: 'procurement@svwafers.com',
    phone: '+1-408-555-0123',
    location: 'San Jose, CA, USA',
    leadTimeWeeks: 4,
    qualityScore: 88,
    reliabilityScore: 85,
    status: 'active',
    itemsSupplied: ['item-001'],
    priceCompetitiveness: 'high',
    lastOrderDate: weeksAgo(9), // ~2 months ago
    totalOrders: 23,
  },
  {
    id: 'sup-003',
    name: 'Kyocera Electronic Components',
    contactEmail: 'orders@kyocera-ec.co.jp',
    phone: '+81-75-555-6789',
    location: 'Kyoto, Japan',
    leadTimeWeeks: 8,
    qualityScore: 98,
    reliabilityScore: 96,
    status: 'active',
    itemsSupplied: ['item-003', 'item-004'],
    priceCompetitiveness: 'low',
    lastOrderDate: weeksAgo(7), // ~7 weeks ago
    totalOrders: 67,
  },
  {
    id: 'sup-004',
    name: 'Copper Tech Solutions',
    contactEmail: 'sales@coppertech.de',
    phone: '+49-89-555-4321',
    location: 'Munich, Germany',
    leadTimeWeeks: 5,
    qualityScore: 91,
    reliabilityScore: 89,
    status: 'active',
    itemsSupplied: ['item-002'],
    priceCompetitiveness: 'medium',
    lastOrderDate: weeksAgo(6), // ~6 weeks ago
    totalOrders: 34,
  },
  {
    id: 'sup-005',
    name: 'Shenzhen Advanced Materials',
    contactEmail: 'export@szadvanced.cn',
    phone: '+86-755-555-8888',
    location: 'Shenzhen, China',
    leadTimeWeeks: 7,
    qualityScore: 82,
    reliabilityScore: 78,
    status: 'active',
    itemsSupplied: ['item-001', 'item-002', 'item-003'],
    priceCompetitiveness: 'high',
    lastOrderDate: weeksAgo(11), // ~2.5 months ago
    totalOrders: 12,
  },
  {
    id: 'sup-006',
    name: 'MetalWorks Inc',
    contactEmail: 'sales@metalworks.com',
    phone: '+1-312-555-7890',
    location: 'Chicago, IL, USA',
    leadTimeWeeks: 2,
    qualityScore: 94,
    reliabilityScore: 91,
    status: 'active',
    itemsSupplied: ['item-001', 'item-002'],
    priceCompetitiveness: 'high',
    lastOrderDate: daysAgo(3),
    totalOrders: 28,
  },
];

// ============ ITEMS (Raw Materials) ============
export const mockItems: Item[] = [
  {
    id: 'item-001',
    name: 'Silicon Wafer (300mm)',
    sku: 'SW-300-PRIME',
    category: 'raw-material',
    description:
      'High-purity silicon wafer, 300mm diameter, prime grade for advanced node fabrication',
    unitOfMeasure: 'wafer',
    unitCost: 450.0,
    leadTimeWeeks: 6,
    minimumOrderQuantity: 100,
    suppliers: ['sup-001', 'sup-002', 'sup-005'],
    requiredPerUnit: 0.1, // 10 chips per wafer
  },
  {
    id: 'item-002',
    name: 'Copper Substrate',
    sku: 'CS-FLIP-01',
    category: 'raw-material',
    description: 'Copper-based flip-chip substrate for BGA packages',
    unitOfMeasure: 'unit',
    unitCost: 12.5,
    leadTimeWeeks: 5,
    minimumOrderQuantity: 500,
    suppliers: ['sup-001', 'sup-004', 'sup-005'],
    requiredPerUnit: 1,
  },
  {
    id: 'item-003',
    name: 'Ceramic Package (CLGA)',
    sku: 'PKG-CLGA-256',
    category: 'component',
    description: 'Ceramic Land Grid Array package, 256 pins',
    unitOfMeasure: 'unit',
    unitCost: 8.75,
    leadTimeWeeks: 7,
    minimumOrderQuantity: 1000,
    suppliers: ['sup-003', 'sup-005'],
    requiredPerUnit: 1,
  },
  {
    id: 'item-004',
    name: 'Thermal Interface Material',
    sku: 'TIM-PREMIUM',
    category: 'component',
    description: 'High-performance thermal paste for heat dissipation',
    unitOfMeasure: 'gram',
    unitCost: 0.15,
    leadTimeWeeks: 3,
    minimumOrderQuantity: 5000,
    suppliers: ['sup-003'],
    requiredPerUnit: 2, // 2 grams per chip
  },
  {
    id: 'item-005',
    name: 'Anti-Static Packaging',
    sku: 'PKG-ESD-TRAY',
    category: 'packaging',
    description: 'ESD-safe chip trays for shipping and storage',
    unitOfMeasure: 'tray',
    unitCost: 2.25,
    leadTimeWeeks: 2,
    minimumOrderQuantity: 200,
    suppliers: ['sup-004'],
    requiredPerUnit: 0.05, // 20 chips per tray
  },
];

// ============ INVENTORY ============
// Status is determined by:
// - shortage > 0 (requiredQuantity > availableStock) → Shortage
// - availableStock < safetyStock → Critical
// - availableStock < reorderPoint (but >= safetyStock) → Low Stock
// - otherwise → Healthy
//
// Target: 2 healthy, 2 low stock, 1 shortage
export const mockInventory: InventoryItem[] = [
  {
    // SHORTAGE: Required ~500 (5000 demand * 0.1), available 200 → shortage 300
    id: 'inv-001',
    itemId: 'item-001',
    currentStock: 250,
    reservedStock: 50,
    availableStock: 200,
    reorderPoint: 500,
    safetyStock: 100,
    lastRestockDate: weeksAgo(7), // ~7 weeks ago
    location: 'Warehouse A - Clean Room',
  },
  {
    // HEALTHY: Required ~5000, available 6000 → no shortage. 6000 >= reorderPoint 4000
    id: 'inv-002',
    itemId: 'item-002',
    currentStock: 6500,
    reservedStock: 500,
    availableStock: 6000,
    reorderPoint: 4000,
    safetyStock: 1000,
    lastRestockDate: weeksAgo(3), // ~3 weeks ago
    location: 'Warehouse A - Shelf B3',
  },
  {
    // LOW STOCK: Required ~5000, available 5500 → no shortage. 5500 < reorderPoint 6000
    id: 'inv-003',
    itemId: 'item-003',
    currentStock: 5800,
    reservedStock: 300,
    availableStock: 5500,
    reorderPoint: 6000,
    safetyStock: 2000,
    lastRestockDate: weeksAgo(5), // ~5 weeks ago
    location: 'Warehouse B - Shelf C1',
  },
  {
    // LOW STOCK: Required ~10000, available 12000 → no shortage. 12000 < reorderPoint 15000
    id: 'inv-004',
    itemId: 'item-004',
    currentStock: 14000,
    reservedStock: 2000,
    availableStock: 12000,
    reorderPoint: 15000,
    safetyStock: 5000,
    lastRestockDate: weeksAgo(4), // ~4 weeks ago
    location: 'Warehouse A - Temperature Controlled',
  },
  {
    // HEALTHY: Required ~250, available 400 → no shortage. 400 >= reorderPoint 300
    id: 'inv-005',
    itemId: 'item-005',
    currentStock: 450,
    reservedStock: 50,
    availableStock: 400,
    reorderPoint: 300,
    safetyStock: 100,
    lastRestockDate: weeksAgo(6), // ~6 weeks ago
    location: 'Warehouse B - Shelf A2',
  },
];

// ============ PURCHASE ORDERS ============
// Generate dynamic PO numbers based on current year
function getPONumber(sequence: number, yearsAgo = 0): string {
  const year = new Date().getFullYear() - yearsAgo;
  return `PO-${year}-${sequence.toString().padStart(4, '0')}`;
}

export const mockPurchaseOrders: PurchaseOrder[] = [
  // Historical/Completed Orders (delivered in the past)
  {
    id: 'po-hist-001',
    poNumber: getPONumber(42, 1), // Last year
    supplierId: 'sup-001',
    status: 'received',
    items: [
      {
        itemId: 'item-001',
        quantity: 150,
        unitPrice: 440.0,
        totalPrice: 66000.0,
      },
    ],
    totalAmount: 66000.0,
    createdAt: monthsAgo(4), // Created 4 months ago
    expectedDelivery: monthsAgo(3), // Expected 3 months ago
    deliveredOn: daysAgo(92), // Delivered ~3 months ago (2 days early)
    notes: 'Initial stock order',
    aiGenerated: false,
  },
  {
    id: 'po-hist-002',
    poNumber: getPONumber(45, 1),
    supplierId: 'sup-003',
    status: 'received',
    items: [
      {
        itemId: 'item-003',
        quantity: 3000,
        unitPrice: 8.25,
        totalPrice: 24750.0,
      },
      {
        itemId: 'item-004',
        quantity: 10000,
        unitPrice: 0.14,
        totalPrice: 1400.0,
      },
    ],
    totalAmount: 26150.0,
    createdAt: monthsAgo(4), // Created 4 months ago
    expectedDelivery: weeksAgo(10), // Expected ~10 weeks ago
    deliveredOn: daysAgo(72), // Delivered ~10 weeks ago (2 days early)
    notes: 'Ceramic packages and thermal material bulk order',
    aiGenerated: false,
  },
  {
    id: 'po-hist-003',
    poNumber: getPONumber(48, 1),
    supplierId: 'sup-004',
    status: 'received',
    items: [
      {
        itemId: 'item-002',
        quantity: 1500,
        unitPrice: 12.0,
        totalPrice: 18000.0,
      },
    ],
    totalAmount: 18000.0,
    createdAt: monthsAgo(3), // Created 3 months ago
    expectedDelivery: weeksAgo(9), // Expected ~9 weeks ago
    deliveredOn: daysAgo(61), // Delivered ~9 weeks ago (2 days late)
    notes: 'Copper substrate replenishment',
    aiGenerated: false,
  },
  {
    id: 'po-hist-004',
    poNumber: getPONumber(51, 1),
    supplierId: 'sup-002',
    status: 'received',
    items: [
      {
        itemId: 'item-001',
        quantity: 100,
        unitPrice: 455.0,
        totalPrice: 45500.0,
      },
    ],
    totalAmount: 45500.0,
    createdAt: weeksAgo(12), // Created 12 weeks ago
    expectedDelivery: weeksAgo(8), // Expected 8 weeks ago
    deliveredOn: daysAgo(57), // Delivered ~8 weeks ago (1 day early)
    notes: 'Emergency silicon wafer order - expedited',
    aiGenerated: true,
  },
  {
    id: 'po-hist-005',
    poNumber: getPONumber(55, 1),
    supplierId: 'sup-005',
    status: 'received',
    items: [
      {
        itemId: 'item-002',
        quantity: 800,
        unitPrice: 11.25,
        totalPrice: 9000.0,
      },
      {
        itemId: 'item-003',
        quantity: 1000,
        unitPrice: 7.5,
        totalPrice: 7500.0,
      },
    ],
    totalAmount: 16500.0,
    createdAt: weeksAgo(11), // Created 11 weeks ago
    expectedDelivery: weeksAgo(5), // Expected 5 weeks ago
    deliveredOn: daysAgo(36), // Delivered ~5 weeks ago (1 day early)
    notes: 'Cost optimization order from Shenzhen supplier',
    aiGenerated: false,
  },
  {
    id: 'po-hist-006',
    poNumber: getPONumber(58, 1),
    supplierId: 'sup-003',
    status: 'received',
    items: [
      {
        itemId: 'item-004',
        quantity: 8000,
        unitPrice: 0.15,
        totalPrice: 1200.0,
      },
    ],
    totalAmount: 1200.0,
    createdAt: weeksAgo(9), // Created 9 weeks ago
    expectedDelivery: weeksAgo(6), // Expected 6 weeks ago
    deliveredOn: daysAgo(44), // Delivered ~6 weeks ago (2 days early)
    notes: 'Thermal interface material restock',
    aiGenerated: false,
  },
  // Active/Current Orders
  {
    id: 'po-001',
    poNumber: getPONumber(1),
    supplierId: 'sup-001',
    status: 'confirmed',
    items: [
      {
        itemId: 'item-001',
        quantity: 200,
        unitPrice: 445.0,
        totalPrice: 89000.0,
      },
      {
        itemId: 'item-002',
        quantity: 1000,
        unitPrice: 12.0,
        totalPrice: 12000.0,
      },
    ],
    totalAmount: 101000.0,
    createdAt: weeksAgo(4), // Created 4 weeks ago
    expectedDelivery: weeksFromNow(2), // Expected in 2 weeks
    notes: 'Q1 replenishment order',
    aiGenerated: false,
  },
  {
    id: 'po-002',
    poNumber: getPONumber(2),
    supplierId: 'sup-003',
    status: 'shipped',
    items: [
      {
        itemId: 'item-003',
        quantity: 2000,
        unitPrice: 8.5,
        totalPrice: 17000.0,
      },
    ],
    totalAmount: 17000.0,
    createdAt: weeksAgo(5), // Created 5 weeks ago
    expectedDelivery: daysFromNow(5), // Expected in 5 days
    aiGenerated: false,
  },
  {
    id: 'po-003',
    poNumber: getPONumber(3),
    supplierId: 'sup-002',
    status: 'draft',
    items: [
      {
        itemId: 'item-001',
        quantity: 300,
        unitPrice: 460.0,
        totalPrice: 138000.0,
      },
    ],
    totalAmount: 138000.0,
    createdAt: daysAgo(5), // Created 5 days ago
    expectedDelivery: weeksFromNow(4), // Expected in 4 weeks
    notes: 'AI-recommended order to meet demand',
    aiGenerated: true,
  },
  {
    id: 'po-004',
    poNumber: getPONumber(4),
    supplierId: 'sup-004',
    status: 'pending',
    items: [
      {
        itemId: 'item-002',
        quantity: 2000,
        unitPrice: 12.25,
        totalPrice: 24500.0,
      },
    ],
    totalAmount: 24500.0,
    createdAt: daysAgo(10), // Created 10 days ago
    expectedDelivery: weeksFromNow(4), // Expected in 4 weeks
    notes: 'Urgent restock - below safety stock',
    aiGenerated: true,
  },
];

// ============ SALES FORECAST ============
// Generate forecast periods dynamically based on current date
function generateForecastPeriods(): SalesForecast[] {
  const now = new Date();
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Forecasted units with slight variation per month
  const baseUnits = [1500, 1800, 1700];
  const baseConfidence = [92, 88, 85];

  return [0, 1, 2].map((offset, index) => {
    const futureDate = new Date(
      now.getFullYear(),
      now.getMonth() + offset + 1,
      1,
    );
    const monthName = months[futureDate.getMonth()];
    const year = futureDate.getFullYear();

    return {
      period: `${monthName} ${year}`,
      forecastedUnits: baseUnits[index],
      confidence: baseConfidence[index],
    };
  });
}

export const mockSalesForecast: SalesForecast[] = generateForecastPeriods();

// ============ SUPPLIER QUOTES ============
export const mockSupplierQuotes: SupplierQuote[] = [
  {
    supplierId: 'sup-001',
    itemId: 'item-001',
    pricePerUnit: 445.0,
    minimumQuantity: 100,
    validUntil: monthsFromNow(3), // Valid for 3 more months
    leadTimeWeeks: 6,
  },
  {
    supplierId: 'sup-002',
    itemId: 'item-001',
    pricePerUnit: 460.0,
    minimumQuantity: 50,
    validUntil: weeksFromNow(6), // Valid for 6 more weeks
    leadTimeWeeks: 4,
  },
  {
    supplierId: 'sup-005',
    itemId: 'item-001',
    pricePerUnit: 420.0,
    minimumQuantity: 200,
    validUntil: monthsFromNow(2), // Valid for 2 more months
    leadTimeWeeks: 7,
  },
  {
    supplierId: 'sup-001',
    itemId: 'item-002',
    pricePerUnit: 12.0,
    minimumQuantity: 500,
    validUntil: monthsFromNow(3), // Valid for 3 more months
    leadTimeWeeks: 5,
  },
  {
    supplierId: 'sup-004',
    itemId: 'item-002',
    pricePerUnit: 12.25,
    minimumQuantity: 500,
    validUntil: weeksFromNow(6), // Valid for 6 more weeks
    leadTimeWeeks: 5,
  },
  {
    supplierId: 'sup-005',
    itemId: 'item-002',
    pricePerUnit: 11.5,
    minimumQuantity: 1000,
    validUntil: monthsFromNow(2), // Valid for 2 more months
    leadTimeWeeks: 6,
  },
  {
    supplierId: 'sup-003',
    itemId: 'item-003',
    pricePerUnit: 8.5,
    minimumQuantity: 1000,
    validUntil: monthsFromNow(4), // Valid for 4 more months
    leadTimeWeeks: 8,
  },
  {
    supplierId: 'sup-005',
    itemId: 'item-003',
    pricePerUnit: 7.8,
    minimumQuantity: 2000,
    validUntil: monthsFromNow(2), // Valid for 2 more months
    leadTimeWeeks: 7,
  },
  {
    supplierId: 'sup-003',
    itemId: 'item-004',
    pricePerUnit: 0.15,
    minimumQuantity: 5000,
    validUntil: monthsFromNow(6), // Valid for 6 more months
    leadTimeWeeks: 3,
  },
];

// ============ DERIVED DATA / CALCULATIONS ============

// Calculate total forecasted demand for the next 3 months
export const totalForecastedDemand = mockSalesForecast.reduce(
  (sum, f) => sum + f.forecastedUnits,
  0,
);

// Calculate material requirements based on forecast
export function calculateMaterialRequirements() {
  return mockItems.map((item) => {
    const inventory = mockInventory.find((inv) => inv.itemId === item.id);
    const requiredQuantity = Math.ceil(
      totalForecastedDemand * item.requiredPerUnit,
    );
    const availableStock = inventory?.availableStock ?? 0;
    const shortage = Math.max(0, requiredQuantity - availableStock);

    return {
      item,
      requiredQuantity,
      availableStock,
      shortage,
      shortagePercentage:
        requiredQuantity > 0
          ? Math.round((shortage / requiredQuantity) * 100)
          : 0,
      needsReorder: shortage > 0,
    };
  });
}

// Get suppliers for a specific item with their quotes
export function getSuppliersForItem(itemId: string) {
  const item = mockItems.find((i) => i.id === itemId);
  if (!item) return [];

  return item.suppliers
    .map((supplierId) => {
      const supplier = mockSuppliers.find((s) => s.id === supplierId);
      const quote = mockSupplierQuotes.find(
        (q) => q.supplierId === supplierId && q.itemId === itemId,
      );
      return { supplier, quote };
    })
    .filter((s) => s.supplier !== undefined);
}

// Production timeline constants
export const PRODUCTION_TIMELINE = {
  averageLeadTimeWeeks: 6, // Raw material procurement
  manufacturingWeeks: 2,
  shippingWeeks: 1,
  totalWeeks: 9, // Total time from order to customer delivery
};

// Calculate inventory coverage (how many units current inventory can produce)
export function calculateInventoryCoverage() {
  const requirements = calculateMaterialRequirements();

  // Find the bottleneck - the material that limits production the most
  let minCoverage = Infinity;
  let bottleneckItem: Item | null = null;

  for (const req of requirements) {
    if (req.item.requiredPerUnit > 0) {
      const coverage = req.availableStock / req.item.requiredPerUnit;
      if (coverage < minCoverage) {
        minCoverage = coverage;
        bottleneckItem = req.item;
      }
    }
  }

  return {
    unitsCanProduce: Math.floor(minCoverage),
    bottleneckItem,
    forecastedDemand: totalForecastedDemand,
    shortfall: Math.max(0, totalForecastedDemand - Math.floor(minCoverage)),
  };
}

// AI Recommendations
export function generateRecommendations() {
  const coverage = calculateInventoryCoverage();
  const requirements = calculateMaterialRequirements();
  const recommendations: Array<{
    type: 'urgent' | 'warning' | 'info';
    title: string;
    description: string;
    action?: string;
  }> = [];

  if (coverage.shortfall > 0) {
    recommendations.push({
      type: 'urgent',
      title: 'Production Shortfall Detected',
      description: `Current inventory can only produce ${coverage.unitsCanProduce.toLocaleString()} units, but ${coverage.forecastedDemand.toLocaleString()} are forecasted. Shortfall: ${coverage.shortfall.toLocaleString()} units.`,
      action: 'Trigger AI Procurement Workflow',
    });
  }

  if (coverage.bottleneckItem) {
    recommendations.push({
      type: 'warning',
      title: `Bottleneck: ${coverage.bottleneckItem.name}`,
      description: `This material is limiting your production capacity. Consider prioritizing orders for this item.`,
    });
  }

  const urgentItems = requirements.filter((r) => r.shortagePercentage > 50);
  if (urgentItems.length > 0) {
    recommendations.push({
      type: 'urgent',
      title: `${urgentItems.length} Items Need Immediate Reorder`,
      description: `These items have more than 50% shortage: ${urgentItems.map((i) => i.item.name).join(', ')}`,
    });
  }

  // Timeline warning
  const weeksUntilDeadline = 12; // 3 months
  if (PRODUCTION_TIMELINE.totalWeeks >= weeksUntilDeadline - 2) {
    recommendations.push({
      type: 'warning',
      title: 'Tight Timeline',
      description: `With ${PRODUCTION_TIMELINE.totalWeeks} weeks total lead time (procurement + manufacturing + shipping), orders should be placed within 2 weeks to meet Q1 demand.`,
    });
  }

  return recommendations;
}

// ============ AI WORKFLOWS ============

export type AIWorkflowStepStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed';
export type AIWorkflowStatus = 'running' | 'completed' | 'failed' | 'cancelled';

export interface AIWorkflowStep {
  id: string;
  name: string;
  description: string;
  status: AIWorkflowStepStatus;
  startedAt?: string;
  completedAt?: string;
  details?: {
    type: 'vendor_analysis' | 'supplier_communication' | 'po_generation';
    data: Record<string, unknown>;
  };
}

export interface AIWorkflow {
  id: string;
  name: string;
  triggerReason: string;
  status: AIWorkflowStatus;
  createdAt: string;
  completedAt?: string;
  currentStepIndex: number;
  steps: AIWorkflowStep[];
  results?: {
    vendorsAnalyzed: number;
    vendorsSelected: number;
    negotiationSavings: number;
    purchaseOrdersGenerated: string[];
    totalPOValue: number;
  };
}

function hoursAgo(hours: number): string {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date.toISOString();
}

function minutesAgo(minutes: number): string {
  const date = new Date();
  date.setMinutes(date.getMinutes() - minutes);
  return date.toISOString();
}

export const mockAIWorkflows: AIWorkflow[] = [
  // Currently running workflow
  {
    id: 'wf-001',
    name: 'Q1 Raw Materials Procurement',
    triggerReason: 'Insufficient inventory to meet 5,000 unit forecast',
    status: 'running',
    createdAt: minutesAgo(12),
    currentStepIndex: 1,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Vendors',
        description:
          'Evaluating supplier capabilities, lead times, and pricing',
        status: 'completed',
        startedAt: minutesAgo(12),
        completedAt: minutesAgo(8),
        details: {
          type: 'vendor_analysis',
          data: {
            vendorsEvaluated: 5,
            criteriaUsed: [
              'lead_time',
              'price',
              'quality_score',
              'reliability',
            ],
            topCandidates: ['sup-001', 'sup-003', 'sup-005'],
          },
        },
      },
      {
        id: 'communicate',
        name: 'Supplier Communication',
        description:
          'Negotiating pricing and confirming delivery timelines with suppliers',
        status: 'running',
        startedAt: minutesAgo(8),
        details: {
          type: 'supplier_communication',
          data: {
            activeNegotiations: 3,
            vendorResponses: [
              {
                supplierId: 'sup-001',
                status: 'counter_offer_received',
                discount: 8,
              },
              { supplierId: 'sup-003', status: 'awaiting_response' },
              { supplierId: 'sup-005', status: 'price_agreed', discount: 12 },
            ],
          },
        },
      },
      {
        id: 'generate',
        name: 'Generate POs',
        description: 'Creating purchase orders for approved vendors',
        status: 'pending',
      },
    ],
  },
  // Completed workflow from earlier today
  {
    id: 'wf-002',
    name: 'Emergency Silicon Wafer Restock',
    triggerReason: 'Safety stock threshold breached for Silicon Wafer',
    status: 'completed',
    createdAt: hoursAgo(6),
    completedAt: hoursAgo(5),
    currentStepIndex: 2,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Vendors',
        description:
          'Evaluating supplier capabilities, lead times, and pricing',
        status: 'completed',
        startedAt: hoursAgo(6),
        completedAt: hoursAgo(5.8),
        details: {
          type: 'vendor_analysis',
          data: {
            vendorsEvaluated: 3,
            criteriaUsed: ['lead_time', 'price', 'availability'],
            topCandidates: ['sup-001', 'sup-003'],
          },
        },
      },
      {
        id: 'communicate',
        name: 'Supplier Communication',
        description:
          'Negotiating pricing and confirming delivery timelines with suppliers',
        status: 'completed',
        startedAt: hoursAgo(5.8),
        completedAt: hoursAgo(5.2),
        details: {
          type: 'supplier_communication',
          data: {
            finalDiscounts: [
              { supplierId: 'sup-001', discount: 10 },
              { supplierId: 'sup-003', discount: 7 },
            ],
            totalSavings: 4250,
            requiredBy: daysFromNow(45),
            confirmedDelivery: daysFromNow(42),
            bufferDays: 3,
            timelineApproved: true,
          },
        },
      },
      {
        id: 'generate',
        name: 'Generate POs',
        description: 'Creating purchase orders for approved vendors',
        status: 'completed',
        startedAt: hoursAgo(5.2),
        completedAt: hoursAgo(5),
        details: {
          type: 'po_generation',
          data: {
            posGenerated: ['PO-2024-008', 'PO-2024-009'],
            totalValue: 42500,
          },
        },
      },
    ],
    results: {
      vendorsAnalyzed: 3,
      vendorsSelected: 2,
      negotiationSavings: 4250,
      purchaseOrdersGenerated: ['PO-2024-008', 'PO-2024-009'],
      totalPOValue: 42500,
    },
  },
  // Completed workflow from yesterday
  {
    id: 'wf-003',
    name: 'Gold Wire Supplier Optimization',
    triggerReason: 'Better pricing available from alternative suppliers',
    status: 'completed',
    createdAt: daysAgo(1) + 'T14:30:00.000Z',
    completedAt: daysAgo(1) + 'T15:15:00.000Z',
    currentStepIndex: 2,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Vendors',
        description:
          'Evaluating supplier capabilities, lead times, and pricing',
        status: 'completed',
        startedAt: daysAgo(1) + 'T14:30:00.000Z',
        completedAt: daysAgo(1) + 'T14:42:00.000Z',
        details: {
          type: 'vendor_analysis',
          data: {
            vendorsEvaluated: 4,
            criteriaUsed: ['price', 'quality_score', 'minimum_order'],
            topCandidates: ['sup-002', 'sup-004'],
          },
        },
      },
      {
        id: 'communicate',
        name: 'Supplier Communication',
        description:
          'Negotiating pricing and confirming delivery timelines with suppliers',
        status: 'completed',
        startedAt: daysAgo(1) + 'T14:42:00.000Z',
        completedAt: daysAgo(1) + 'T15:05:00.000Z',
        details: {
          type: 'supplier_communication',
          data: {
            finalDiscounts: [{ supplierId: 'sup-002', discount: 15 }],
            totalSavings: 8750,
            requiredBy: daysFromNow(30),
            confirmedDelivery: daysFromNow(25),
            bufferDays: 5,
            timelineApproved: true,
          },
        },
      },
      {
        id: 'generate',
        name: 'Generate POs',
        description: 'Creating purchase orders for approved vendors',
        status: 'completed',
        startedAt: daysAgo(1) + 'T15:05:00.000Z',
        completedAt: daysAgo(1) + 'T15:15:00.000Z',
        details: {
          type: 'po_generation',
          data: {
            posGenerated: ['PO-2024-007'],
            totalValue: 58350,
          },
        },
      },
    ],
    results: {
      vendorsAnalyzed: 4,
      vendorsSelected: 1,
      negotiationSavings: 8750,
      purchaseOrdersGenerated: ['PO-2024-007'],
      totalPOValue: 58350,
    },
  },
  // Failed workflow
  {
    id: 'wf-004',
    name: 'Ceramic Substrate Bulk Order',
    triggerReason: 'Bulk discount opportunity identified',
    status: 'failed',
    createdAt: daysAgo(3) + 'T09:00:00.000Z',
    completedAt: daysAgo(3) + 'T09:35:00.000Z',
    currentStepIndex: 1,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Vendors',
        description:
          'Evaluating supplier capabilities, lead times, and pricing',
        status: 'completed',
        startedAt: daysAgo(3) + 'T09:00:00.000Z',
        completedAt: daysAgo(3) + 'T09:12:00.000Z',
        details: {
          type: 'vendor_analysis',
          data: {
            vendorsEvaluated: 2,
            criteriaUsed: ['price', 'bulk_discount', 'availability'],
            topCandidates: ['sup-004'],
          },
        },
      },
      {
        id: 'communicate',
        name: 'Supplier Communication',
        description:
          'Negotiating pricing and confirming delivery timelines with suppliers',
        status: 'failed',
        startedAt: daysAgo(3) + 'T09:12:00.000Z',
        completedAt: daysAgo(3) + 'T09:35:00.000Z',
        details: {
          type: 'supplier_communication',
          data: {
            finalDiscounts: [{ supplierId: 'sup-004', discount: 5 }],
            totalSavings: 2100,
            requiredBy: daysFromNow(21),
            confirmedDelivery: daysFromNow(35),
            bufferDays: -14,
            timelineApproved: false,
            failureReason: 'Supplier cannot meet required delivery timeline',
          },
        },
      },
      {
        id: 'generate',
        name: 'Generate POs',
        description: 'Creating purchase orders for approved vendors',
        status: 'pending',
      },
    ],
  },
  // Cancelled workflow
  {
    id: 'wf-005',
    name: 'Copper Lead Frame Reorder',
    triggerReason: 'Monthly automated reorder trigger',
    status: 'cancelled',
    createdAt: daysAgo(5) + 'T11:00:00.000Z',
    completedAt: daysAgo(5) + 'T11:05:00.000Z',
    currentStepIndex: 0,
    steps: [
      {
        id: 'analyze',
        name: 'Analyze Vendors',
        description:
          'Evaluating supplier capabilities, lead times, and pricing',
        status: 'completed',
        startedAt: daysAgo(5) + 'T11:00:00.000Z',
        completedAt: daysAgo(5) + 'T11:05:00.000Z',
        details: {
          type: 'vendor_analysis',
          data: {
            vendorsEvaluated: 3,
            note: 'Cancelled by user - manual order already placed',
          },
        },
      },
      {
        id: 'communicate',
        name: 'Supplier Communication',
        description:
          'Negotiating pricing and confirming delivery timelines with suppliers',
        status: 'pending',
      },
      {
        id: 'generate',
        name: 'Generate POs',
        description: 'Creating purchase orders for approved vendors',
        status: 'pending',
      },
    ],
  },
];

// Dashboard stats
export function getDashboardStats() {
  const coverage = calculateInventoryCoverage();
  const requirements = calculateMaterialRequirements();
  const activeOrders = mockPurchaseOrders.filter(
    (po) => !['received', 'cancelled'].includes(po.status),
  );

  // Calculate total inventory units
  const totalInventoryUnits = mockInventory.reduce(
    (sum, inv) => sum + inv.currentStock,
    0,
  );

  // Count running AI workflows
  const runningAIAgents = mockAIWorkflows.filter(
    (w) => w.status === 'running',
  ).length;

  // Count low stock alerts (items below reorder point)
  const lowStockAlerts = mockInventory.filter((inv) => {
    return inv.currentStock <= inv.reorderPoint;
  }).length;

  return {
    forecastedDemand: totalForecastedDemand,
    productionCapacity: coverage.unitsCanProduce,
    shortfall: coverage.shortfall,
    activeSuppliers: mockSuppliers.filter((s) => s.status === 'active').length,
    pendingOrders: activeOrders.length,
    pendingOrdersValue: activeOrders.reduce(
      (sum, po) => sum + po.totalAmount,
      0,
    ),
    itemsNeedingReorder: requirements.filter((r) => r.needsReorder).length,
    averageLeadTime: PRODUCTION_TIMELINE.averageLeadTimeWeeks,
    totalInventoryUnits,
    runningAIAgents,
    lowStockAlerts,
  };
}

// Get product inventory with item details for display
export function getProductInventory() {
  return mockInventory.map((inv) => {
    const item = mockItems.find((i) => i.id === inv.itemId);
    const stockPercentage =
      inv.reorderPoint > 0
        ? Math.min(
            100,
            Math.round((inv.currentStock / (inv.reorderPoint * 2)) * 100),
          )
        : 100;

    let status: 'healthy' | 'low' | 'critical' = 'healthy';
    if (inv.currentStock <= inv.safetyStock) {
      status = 'critical';
    } else if (inv.currentStock <= inv.reorderPoint) {
      status = 'low';
    }

    return {
      ...inv,
      item,
      stockPercentage,
      status,
    };
  });
}
